import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

const StoreActions = ({ open, onClose, workOrder, onSuccess }) => {
  const [decision, setDecision] = useState('ACCEPT');
  const [notes, setNotes] = useState('');
  const [availableFromDate, setAvailableFromDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (decision === 'REJECT' && !notes.trim()) {
        setError('Reason is required when setting parts availability date');
        return;
      }

      const endpoint = decision === 'ACCEPT' 
        ? `/api/maintenance/${workOrder._id}/store/accept`
        : `/api/maintenance/${workOrder._id}/store/reject`;

      await axios.post(endpoint, {
        notes: notes.trim() || undefined,
        availableFromDate: decision === 'REJECT' ? availableFromDate || undefined : undefined
      });

      showToast(
        decision === 'ACCEPT'
          ? 'Work order accepted successfully'
          : 'Availability date submitted. Planning must reschedule after this date.',
        'success'
      );
      onSuccess();
      handleClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message || `Failed to ${decision === 'ACCEPT' ? 'accept' : 'reject'} work order`;
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDecision('ACCEPT');
    setNotes('');
    setAvailableFromDate('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Store Action</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Work Order: <strong>{workOrder?.title}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aircraft: <strong>{workOrder?.aircraftId?.registration || 'Unknown'}</strong>
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Decision
          </Typography>
          <ToggleButtonGroup
            value={decision}
            exclusive
            onChange={(e, newValue) => newValue && setDecision(newValue)}
            fullWidth
          >
            <ToggleButton value="ACCEPT">Accept</ToggleButton>
            <ToggleButton value="REJECT">Set Parts Availability Date</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={4}
          label={decision === 'REJECT' ? 'Reason / Notes (Required)' : 'Notes (Optional)'}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={decision === 'REJECT' ? 'Explain why parts are currently unavailable...' : 'Add any notes or comments...'}
          required={decision === 'REJECT'}
          sx={{ mt: 2 }}
        />
        {decision === 'REJECT' && (
          <TextField
            fullWidth
            type="date"
            label="Unavailable Parts Available From (Required)"
            value={availableFromDate}
            onChange={(e) => setAvailableFromDate(e.target.value)}
            required
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || (decision === 'REJECT' && (!notes.trim() || !availableFromDate))}
          color={decision === 'REJECT' ? 'warning' : 'primary'}
        >
          {loading ? 'Processing...' : decision === 'ACCEPT' ? 'Accept' : 'Set Availability Date'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StoreActions;

